import { useState, MouseEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  SignedIn,
  SignedOut,
  useClerk,
  useUser,
} from '@clerk/clerk-react'
import { Menu, X, LayoutDashboard, BookOpen, Search, User, Settings, LogOut, Star, ChevronUp, ChevronDown, Shield, Briefcase, MessageCircle, ChevronRight, Tag, Brain } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BrainLogo } from '@/components/ui/brain-logo'
import { cn } from '@/lib/utils'
import { useRole } from '@/hooks/use-role'
import { useUnreadCount } from '@/lib/hooks/use-unread-count'
import { useFreelancerProfile } from '@/lib/hooks/use-freelancer-profile'
import { useClientProfile } from '@/lib/hooks/use-client-profile'
import type { UserRole } from '@/types/role'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

// Landing page nav items for signed-out users
const landingNavItems = [
  { label: 'For Experts', href: '#experts' },
  { label: 'For Clients', href: '#clients' },
  { label: 'About', href: '#founder' },
]

// Freelancer nav items - only routes that exist
const freelancerNavItems = [
  { to: '/genius/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/genius/courses', label: 'Learning', icon: BookOpen },
  { to: '/genius/chat', label: 'Messages', icon: MessageCircle },
  { to: '/genius/profile', label: 'Profile', icon: User },
]

// Client nav items - Find Talent IS the dashboard
const clientNavItems = [
  { to: '/client/dashboard', label: 'Find Talent', icon: Search },
  { to: '/client/featured', label: 'Featured', icon: Star },
  { to: '/client/chat', label: 'Messages', icon: MessageCircle },
  { to: '/client/profile', label: 'Profile', icon: User },
]

// Admin nav items - these show as tabs in the navbar
type AdminNavItem = 
  | { to: string; label: string; icon: typeof User }
  | { label: string; icon: typeof MessageCircle; submenu: Array<{ to: string; label: string; icon: typeof MessageCircle | typeof Settings | typeof BookOpen }> }

// Type guard to check if item has 'to' property
const hasTo = (item: AdminNavItem): item is { to: string; label: string; icon: typeof User } => {
  return 'to' in item
}

const adminNavItems: AdminNavItem[] = [
  { to: '/admin?tab=freelancers', label: 'Geniuses', icon: User },
  { to: '/admin?tab=clients', label: 'Clients', icon: User },
  { to: '/admin?tab=courses', label: 'Courses', icon: BookOpen },
  { to: '/admin?tab=searches', label: 'Searches', icon: Search },
  { to: '/admin?tab=skills', label: 'Skills', icon: Settings },
  {
    label: 'Conversations',
    icon: MessageCircle,
    submenu: [
      { to: '/admin?tab=chat-analytics', label: 'Chat Analytics', icon: MessageCircle },
      { to: '/admin?tab=chat-config', label: 'Chat Config', icon: Settings },
      // { to: '/admin?tab=chat-viewer', label: 'Chat Viewer', icon: MessageCircle },
      { to: '/admin?tab=tags', label: 'Tags', icon: Tag },
    ],
  },
]

// Role configuration for display
const roleConfig: Record<UserRole, { label: string; icon: typeof Shield; color: string }> = {
  client: {
    label: 'Client',
    icon: Briefcase,
    color: 'text-[#4669ed]',
  },
  freelancer: {
    label: 'Genius',
    icon: Brain,
    color: 'text-[#4669ed]',
  },
  admin: {
    label: 'Admin',
    icon: Shield,
    color: 'text-[#4669ed]',
  },
}

export function GlobalNavigation() {
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)
  const location = useLocation()
  const { activeRole, availableRoles, setActiveRole, freelancerProfileId, clientProfileId } = useRole()
  const { signOut, openUserProfile } = useClerk()
  const { user } = useUser()
  const { totalUnread } = useUnreadCount()

  // Fetch profiles - check both if available, but prioritize based on active role
  const { freelancerProfile } = useFreelancerProfile(
    freelancerProfileId
  )
  const { clientProfile } = useClientProfile(
    clientProfileId ?? null
  )

  // Get profile picture URL from database, fallback to Clerk's imageUrl
  // Priority: active role profile > other profile > Clerk's imageUrl
  const getProfilePictureUrl = () => {
    // First, check the profile matching the active role
    if (activeRole === 'freelancer' && freelancerProfile?.profile_picture_url) {
      return freelancerProfile.profile_picture_url
    }
    if (activeRole === 'client' && clientProfile?.profile_picture_url) {
      return clientProfile.profile_picture_url
    }
    // If active role doesn't have a picture, check the other profile (for users with multiple roles)
    if (activeRole !== 'freelancer' && freelancerProfile?.profile_picture_url) {
      return freelancerProfile.profile_picture_url
    }
    if (activeRole !== 'client' && clientProfile?.profile_picture_url) {
      return clientProfile.profile_picture_url
    }
    // Fallback to Clerk's imageUrl if no profile picture in database
    return user?.imageUrl
  }

  const profilePictureUrl = getProfilePictureUrl()

  // Get display name - priority: profile display_name > Clerk fullName > Clerk firstName > 'User'
  const getDisplayName = () => {
    // First, check the profile matching the active role
    if (activeRole === 'freelancer' && freelancerProfile?.display_name) {
      return freelancerProfile.display_name
    }
    if (activeRole === 'client' && clientProfile?.company_name) {
      return clientProfile.company_name
    }
    // If active role doesn't have a name, check the other profile (for users with multiple roles)
    if (activeRole !== 'freelancer' && freelancerProfile?.display_name) {
      return freelancerProfile.display_name
    }
    if (activeRole !== 'client' && clientProfile?.company_name) {
      return clientProfile.company_name
    }
    // Fallback to Clerk's user data
    if (user?.fullName) {
      return user.fullName
    }
    if (user?.firstName) {
      return user.firstName
    }
    // Last resort fallback
    return 'User'
  }

  const displayName = getDisplayName()

  // Check if we're on the landing page
  const isLandingPage = location.pathname === '/'

  // Get navigation items based on role
  const getNavItems = () => {
    switch (activeRole) {
      case 'freelancer':
        return freelancerNavItems
      case 'client':
        return clientNavItems
      case 'admin':
        return adminNavItems
      default:
        return []
    }
  }

  // Get dashboard route based on active role
  const getDashboardRoute = () => {
    switch (activeRole) {
      case 'freelancer':
        return '/genius/dashboard'
      case 'client':
        return '/client/dashboard'
      case 'admin':
        return '/admin'
      default:
        return '/client/dashboard'
    }
  }

  // Get user initials for avatar
  const getInitials = () => {
    if (!user) return 'U'
    const firstName = user.firstName || ''
    const lastName = user.lastName || ''
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase()
    }
    return (firstName[0] || user.primaryEmailAddress?.emailAddress?.[0] || 'U').toUpperCase()
  }

  // Handle smooth scroll for landing page anchors
  const handleSmoothScroll = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('#')) return
    event.preventDefault()
    const targetId = href.slice(1)
    const targetElement = document.getElementById(targetId)
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' })
    } else {
      window.location.hash = href
    }
  }

  // Check if a nav item is active
  const isActive = (to: string) => {
    // Handle admin tabs with query params
    if (to.startsWith('/admin?tab=')) {
      const tabParam = to.split('tab=')[1]
      const currentTab = new URLSearchParams(location.search).get('tab') || 'freelancers'
      return location.pathname === '/admin' && currentTab === tabParam
    }
    if (to.endsWith('/dashboard') || to === '/admin') {
      return location.pathname === to
    }
    return location.pathname.startsWith(to)
  }

  // Check if any submenu item is active (for Chat dropdown)
  const isSubmenuActive = (submenu: Array<{ to: string; label: string; icon: typeof MessageCircle }>) => {
    return submenu.some(item => isActive(item.to))
  }

  const navItems = getNavItems()

  // Check if we're on an admin page
  const isAdminPage = location.pathname.startsWith('/admin')

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/60 bg-white/95 backdrop-blur-sm supports-[backdrop-filter]:bg-white/90">
      <div className={cn(
        "mx-auto flex h-20 w-full items-center justify-between px-4 sm:px-6 lg:px-8",
        isAdminPage ? "max-w-[1600px]" : "max-w-7xl"
      )}>
        {/* Logo */}
        <Link to={getDashboardRoute()} className="flex items-center gap-3">
          <BrainLogo size="lg" variant="light" showGlow={false} />
        </Link>

        {/* Desktop Navigation */}
        <SignedOut>
          {/* Landing page nav for signed-out users */}
          <nav className="hidden items-center gap-8 text-sm font-semibold text-slate-600 md:flex">
            {landingNavItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="transition hover:text-slate-900"
                onClick={(event) => handleSmoothScroll(event, item.href)}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </SignedOut>

        <SignedIn>
          {/* Show landing nav items on landing page, app nav elsewhere */}
          {isLandingPage ? (
            <nav className="hidden items-center gap-8 text-sm font-semibold text-slate-600 md:flex">
              {landingNavItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="transition hover:text-slate-900"
                  onClick={(event) => handleSmoothScroll(event, item.href)}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          ) : activeRole === 'admin' ? (
            // Admin navigation - text only, no icons (matching UI statics)
            <nav className="hidden items-center gap-8 md:flex">
              {navItems.map((item) => {
                // Check if item has submenu
                if (!hasTo(item)) {
                  const hasActiveSubmenu = isSubmenuActive(item.submenu)
                  return (
                    <DropdownMenu key={item.label}>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={cn(
                            "text-sm font-semibold transition-colors flex items-center gap-1",
                            hasActiveSubmenu
                              ? "text-[#4669ed]"
                              : "text-slate-700 hover:text-slate-900"
                          )}
                        >
                          {item.label}
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-48">
                        {item.submenu.map((subItem) => {
                          const active = isActive(subItem.to)
                          return (
                            <DropdownMenuItem key={subItem.to} asChild>
                              <Link
                                to={subItem.to}
                                className={cn(
                                  "cursor-pointer",
                                  active && "text-[#4669ed] font-semibold"
                                )}
                              >
                                {subItem.label}
                              </Link>
                            </DropdownMenuItem>
                          )
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )
                }
                // Regular nav item
                const active = isActive(item.to)
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "text-sm font-semibold transition-colors",
                      active
                        ? "text-[#4669ed]"
                        : "text-slate-700 hover:text-slate-900"
                    )}
                  >
                    {item.label}
                  </Link>
                )
              })}
            </nav>
          ) : (
            <nav className="hidden items-center gap-8 md:flex">
              {navItems.map((item) => {
                // For non-admin roles, all items have 'to' property
                if (!('to' in item)) return null
                const Icon = item.icon
                const active = isActive(item.to)
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "flex items-center gap-2 text-[15px] font-semibold transition-colors relative",
                      active
                        ? "text-[#4669ed]"
                        : "text-slate-500 hover:text-slate-900"
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                    {item.label === 'Messages' && totalUnread > 0 && (
                      <span className="absolute -top-1 -right-5 flex min-w-[20px] h-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1.5 leading-none">
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    )}
                  </Link>
                )
              })}
            </nav>
          )}
        </SignedIn>

        {/* Right Side */}
        <div className="hidden items-center gap-3 md:flex">
          <SignedOut>
            <Link to="/sign-in">
              <Button variant="ghost" className="text-slate-700 hover:text-slate-900">
                Sign In
              </Button>
            </Link>
            <Link to="/sign-up">
              <Button className="rounded-full bg-[#4669ed] px-6 font-semibold text-white hover:bg-[#2b42a3]">
                Apply Now
              </Button>
            </Link>
          </SignedOut>

          <SignedIn>
            {/* Go to Dashboard button on landing page */}
            {isLandingPage && (
              <Link to={getDashboardRoute()}>
                <Button className="rounded-full bg-[#4669ed] px-6 font-semibold text-white hover:bg-[#2b42a3]">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Go to Dashboard
                </Button>
              </Link>
            )}

            {/* Role Switcher - show if user has multiple roles */}
            {activeRole && availableRoles.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                    {(() => {
                      const config = roleConfig[activeRole]
                      const Icon = config.icon
                      return (
                        <>
                          <Icon className={cn("h-4 w-4", config.color)} />
                          <span className={cn("text-sm font-semibold", config.color)}>
                            {displayName}
                          </span>
                        </>
                      )
                    })()}
                    <div className="flex flex-col -space-y-1">
                      <ChevronUp className="h-3 w-3 text-slate-400" />
                      <ChevronDown className="h-3 w-3 text-slate-400" />
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500">
                    Switch Role
                  </div>
                  <DropdownMenuSeparator />
                  {availableRoles.map((role) => {
                    const config = roleConfig[role]
                    const Icon = config.icon
                    return (
                      <DropdownMenuItem
                        key={role}
                        onClick={() => setActiveRole(role)}
                        className="cursor-pointer flex items-center gap-2"
                      >
                        <Icon className={cn("h-4 w-4", config.color)} />
                        <span className={role === activeRole ? 'font-semibold' : ''}>
                          {config.label}
                        </span>
                        {role === activeRole && (
                          <span className="ml-auto text-xs text-slate-400">Active</span>
                        )}
                      </DropdownMenuItem>
                    )
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Single role badge - show if user has only one role */}
            {activeRole && availableRoles.length === 1 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100">
                {(() => {
                  const config = roleConfig[activeRole]
                  const Icon = config.icon
                  return (
                    <>
                      <Icon className={cn("h-4 w-4", config.color)} />
                      <span className={cn("text-sm font-semibold", config.color)}>
                        {displayName}
                      </span>
                    </>
                  )
                })()}
              </div>
            )}

            {/* User Avatar */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative">
                  <Avatar className="h-12 w-12 border-2 border-[#4669ed] cursor-pointer transition-opacity hover:opacity-90">
                    <AvatarImage src={profilePictureUrl} alt={user?.fullName || 'User'} />
                    <AvatarFallback className="bg-[#4669ed] text-white font-bold">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={getDashboardRoute()}>
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={activeRole === 'freelancer' ? '/genius/profile' : '/client/profile'}>
                    <User className="mr-2 h-4 w-4" />
                    My Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => openUserProfile()}
                  className="cursor-pointer"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => signOut()}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SignedIn>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden"
          onClick={() => setIsMobileOpen((prev) => !prev)}
          aria-label="Toggle navigation menu"
        >
          {isMobileOpen ? (
            <X className="h-6 w-6 text-slate-900" />
          ) : (
            <Menu className="h-6 w-6 text-slate-900" />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div
        className={cn(
          'absolute left-0 right-0 top-full md:hidden border-t border-slate-100 bg-white px-4 pb-6 pt-2 shadow-lg z-40 transition-all duration-200 ease-in-out max-h-[calc(100vh-5rem)] overflow-y-auto',
          isMobileOpen
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-2 pointer-events-none'
        )}
      >
        <SignedOut>
          <nav className="flex flex-col gap-4 pb-6 pt-4 text-sm font-medium text-slate-700">
            {landingNavItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="rounded-lg px-3 py-2 hover:bg-slate-50"
                onClick={(event) => {
                  handleSmoothScroll(event, item.href)
                  setIsMobileOpen(false)
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="flex flex-col gap-3">
            <Link to="/sign-in" onClick={() => setIsMobileOpen(false)}>
              <Button variant="outline" className="w-full rounded-full border-slate-200 text-slate-900">
                Sign In
              </Button>
            </Link>
            <Link to="/sign-up" onClick={() => setIsMobileOpen(false)}>
              <Button className="w-full rounded-full bg-[#4669ed] hover:bg-[#2b42a3]">
                Apply Now
              </Button>
            </Link>
          </div>
        </SignedOut>

        <SignedIn>
          {/* Go to Dashboard on mobile for landing page */}
          {isLandingPage && (
            <div className="pb-4 pt-2">
              <Link
                to={getDashboardRoute()}
                className="flex items-center justify-center gap-2 rounded-full bg-[#4669ed] px-6 py-3 text-sm font-semibold text-white"
                onClick={() => setIsMobileOpen(false)}
              >
                <LayoutDashboard className="h-4 w-4" />
                Go to Dashboard
              </Link>
            </div>
          )}

          <nav className="flex flex-col gap-2 pb-6 pt-4">
            {navItems.map((item) => {
              // Check if item has submenu (for admin role)
              if (activeRole === 'admin' && !hasTo(item)) {
                const hasActiveSubmenu = isSubmenuActive(item.submenu)
                const isSubmenuOpen = openSubmenu === item.label
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => setOpenSubmenu(isSubmenuOpen ? null : item.label)}
                      className={cn(
                        "flex items-center justify-between w-full rounded-lg px-3 py-3 text-sm font-semibold transition-colors",
                        hasActiveSubmenu
                          ? "bg-[#4669ed]/10 text-[#4669ed]"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span>{item.label}</span>
                      <ChevronRight
                        className={cn(
                          "h-4 w-4 transition-transform",
                          isSubmenuOpen && "rotate-90"
                        )}
                      />
                    </button>
                    {isSubmenuOpen && (
                      <div className="ml-4 mt-1 flex flex-col gap-1 border-l-2 border-slate-200 pl-4">
                        {item.submenu.map((subItem) => {
                          const active = isActive(subItem.to)
                          return (
                            <Link
                              key={subItem.to}
                              to={subItem.to}
                              className={cn(
                                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                                active
                                  ? "bg-[#4669ed]/10 text-[#4669ed]"
                                  : "text-slate-600 hover:bg-slate-50"
                              )}
                              onClick={() => {
                                setIsMobileOpen(false)
                                setOpenSubmenu(null)
                              }}
                            >
                              {subItem.label}
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              }
              // Regular nav item
              if (hasTo(item)) {
                const Icon = item.icon
                const active = isActive(item.to)
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold transition-colors relative",
                      active
                        ? "bg-[#4669ed]/10 text-[#4669ed]"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                    onClick={() => setIsMobileOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                    {item.label === 'Messages' && totalUnread > 0 && (
                      <span className="ml-auto flex min-w-[20px] h-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1.5 leading-none">
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    )}
                  </Link>
                )
              }
              return null
            })}
          </nav>
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center gap-3 px-3 py-2">
              <Avatar className="h-10 w-10 border-2 border-[#4669ed]">
                <AvatarImage src={profilePictureUrl} alt={user?.fullName || 'User'} />
                <AvatarFallback className="bg-[#4669ed] text-white font-bold text-sm">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900">{displayName}</p>
                <p className="text-xs text-slate-500">{user?.primaryEmailAddress?.emailAddress}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <Link
                to={activeRole === 'freelancer' ? '/genius/profile' : '/client/profile'}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => setIsMobileOpen(false)}
              >
                <User className="h-4 w-4" />
                My Profile
              </Link>
              <button
                onClick={() => {
                  openUserProfile()
                  setIsMobileOpen(false)
                }}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 text-left"
              >
                <Settings className="h-4 w-4" />
                Account Settings
              </button>
              <button
                onClick={() => signOut()}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>
        </SignedIn>
      </div>
    </header>
  )
}
